<template>
  <div class="attachment-list">
    <v-expansion-panels v-if="attachments.length" dark flat class="attachment-panels">
      <v-expansion-panel v-for="(attachment, index) in attachments" :key="attachment.fileId || index" class="attachment-panel">
        <v-expansion-panel-header class="attachment-header">
          <div class="attachment-title">
            <span class="attachment-icon" aria-hidden="true">
              <v-icon>mdi-file-document-outline</v-icon>
            </span>
            <span class="attachment-name text-truncate">{{ attachment.originalName }}</span>
          </div>
          <template v-slot:actions>
            <v-btn
              icon
              class="attachment-action"
              @click.stop="downloadFile(attachment)"
              title="Herunterladen"
              :aria-label="`${attachment.originalName} herunterladen`">
              <v-icon>mdi-download-outline</v-icon>
            </v-btn>
            <v-icon class="attachment-chevron">$expand</v-icon>
          </template>
        </v-expansion-panel-header>

        <v-expansion-panel-content class="attachment-content">
          <div v-if="canBePreviewed(attachment.mimeType)" class="preview-content">
            <v-btn
              v-if="!previewUrls[attachment.fileId]"
              outlined
              :loading="loadingStates[attachment.fileId]"
              class="preview-button"
              @click="loadPreviewUrl(attachment)">
              <v-icon left>mdi-eye-outline</v-icon>
              Vorschau laden
            </v-btn>

            <div v-else class="preview-surface">
              <iframe
                v-if="isPdf(attachment.mimeType)"
                :src="previewUrls[attachment.fileId]"
                :title="`Vorschau von ${attachment.originalName}`"
                class="preview-frame"></iframe>
              <img v-else :src="previewUrls[attachment.fileId]" :alt="`Vorschau von ${attachment.originalName}`" class="preview-image" />
            </div>
          </div>
          <div v-else class="preview-unavailable">
            <span class="preview-unavailable__icon"><v-icon>mdi-file-download-outline</v-icon></span>
            <div>
              <strong>Keine Vorschau verfügbar</strong>
              <p>Bitte lade die Datei herunter, um sie zu öffnen.</p>
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <div v-else class="attachment-empty">
      <v-icon>mdi-paperclip</v-icon>
      <span>Keine Anhänge vorhanden</span>
    </div>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "AttachmentList",
  props: {
    applicationId: {
      type: String,
      required: true,
    },
    attachments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      previewUrls: {},
      loadingStates: {},
    }
  },
  watch: {
    applicationId() {
      this.cleanupUrls()
    },
    attachments() {
      this.cleanupUrls()
    },
  },
  methods: {
    canBePreviewed(mimeType) {
      if (!mimeType) return false
      return mimeType === "application/pdf" || mimeType.startsWith("image/")
    },
    isPdf(mimeType) {
      return mimeType === "application/pdf"
    },
    async loadPreviewUrl(attachment) {
      this.$set(this.loadingStates, attachment.fileId, true)
      try {
        const blobUrl = await api.jobApplications.previewAttachment(this.applicationId, attachment.fileId)
        this.$set(this.previewUrls, attachment.fileId, blobUrl)
      } catch (error) {
        console.error("Vorschau konnte nicht geladen werden", error)
        if (this.$toast) this.$toast.error("Vorschau konnte nicht geladen werden")
      } finally {
        this.$set(this.loadingStates, attachment.fileId, false)
      }
    },
    async downloadFile(attachment) {
      try {
        await api.jobApplications.downloadAttachment(this.applicationId, attachment.fileId, attachment.originalName)
      } catch (error) {
        console.error("Download fehlgeschlagen", error)
        if (this.$toast) this.$toast.error("Download fehlgeschlagen")
      }
    },
    cleanupUrls() {
      Object.values(this.previewUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url)
      })
      this.previewUrls = {}
      this.loadingStates = {}
    },
  },
  beforeDestroy() {
    this.cleanupUrls()
  },
}
</script>

<style scoped>
.attachment-list {
  --attachment-primary: #009640;
  --attachment-border: rgba(255, 255, 255, 0.1);
  color: #f4f4f5;
}

.attachment-panels {
  display: grid;
  gap: 10px;
}

.attachment-panel {
  overflow: hidden;
  border: 1px solid var(--attachment-border);
  border-radius: 16px !important;
  color: #f4f4f5;
  background: rgba(255, 255, 255, 0.035) !important;
  box-shadow: none;
}

.attachment-panel::before {
  box-shadow: none !important;
}

.attachment-header {
  min-height: 64px;
  padding: 10px 12px 10px 14px !important;
  color: #e7e7e9;
  font-size: 13px;
  font-weight: 650;
}

.attachment-header:hover {
  background: rgba(255, 255, 255, 0.035);
}

.attachment-title {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.attachment-icon,
.preview-unavailable__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 12px;
  color: #d8f3e2;
  background: rgba(0, 150, 64, 0.13);
}

.attachment-icon .v-icon,
.preview-unavailable__icon .v-icon {
  color: inherit;
  font-size: 20px;
}

.attachment-name {
  min-width: 0;
}

.attachment-action {
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  margin-right: 2px;
  border-radius: 13px;
  color: #65ca8b !important;
}

.attachment-action:hover,
.attachment-action:focus-visible {
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.16);
}

.attachment-chevron {
  color: #8e8e93 !important;
}

.attachment-content ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 14px 14px;
}

.preview-content {
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  text-align: center;
}

.preview-button {
  min-height: 44px;
  border-color: rgba(0, 150, 64, 0.4) !important;
  border-radius: 13px;
  color: #65ca8b !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.preview-button:hover,
.preview-button:focus-visible {
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.14);
}

.preview-surface {
  position: relative;
  display: flex;
  width: 100%;
  height: clamp(320px, 50vh, 640px);
  margin-top: 2px;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.02) 25%, transparent 25%, transparent 75%, rgba(255, 255, 255, 0.02) 75%), #171718;
  background-position: 0 0, 10px 10px;
  background-size: 20px 20px;
}

.preview-frame {
  width: 100%;
  height: 100%;
  border: 0;
  background: #ffffff;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.preview-unavailable,
.attachment-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  color: #a3a3a8;
  background: rgba(0, 0, 0, 0.12);
}

.preview-unavailable {
  justify-content: flex-start;
  margin-top: 12px;
}

.preview-unavailable strong {
  display: block;
  color: #d8d8db;
  font-size: 13px;
}

.preview-unavailable p {
  margin: 3px 0 0;
  color: #929297;
  font-size: 12px;
  line-height: 1.4;
}

.attachment-empty {
  min-height: 92px;
  font-size: 13px;
}

.attachment-empty .v-icon {
  color: #77777c;
}

.attachment-list ::v-deep .v-btn:focus-visible,
.attachment-list ::v-deep .v-expansion-panel-header:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.25) !important;
}

@media (max-width: 599px) {
  .attachment-header {
    min-height: 60px;
    padding-inline: 10px !important;
  }

  .attachment-icon {
    width: 36px;
    height: 36px;
  }

  .preview-surface {
    height: min(56vh, 520px);
    min-height: 280px;
  }

  .preview-unavailable {
    align-items: flex-start;
    padding: 16px;
  }
}
</style>
